import { useEffect, useState } from "react";

/**
 * BrandedLoader — full-viewport RIM-branded loading screen.
 *
 * Design contract:
 *   • Centered logo (public/logo.png) sits inside a slowly-rotating
 *     crimson conic ring so the RIM mark itself stays perfectly still
 *     while the halo animates — feels premium, not spinner-y.
 *   • A soft breathing pulse under the mark hints at "work in progress"
 *     without competing with the ring for attention.
 *   • Copy is monospaced, all-caps, tracked out — matches the rest of
 *     the app's "industrial terminal" vocabulary.
 *
 * Props:
 *   • label   — small caption under the mark (default: "Loading")
 *   • fullscreen — when true, occupies the full viewport (route-level
 *     Suspense). When false, fits inside its parent (inline usage).
 *   • size    — logo size in px (default 72). The ring scales with it.
 */
export default function BrandedLoader({
  label = "Loading",
  fullscreen = true,
  size = 72,
}) {
  // Cycle "Loading" → "Loading." → "Loading.." → "Loading..." so the
  // wait always feels responsive even when the network is stalled.
  const [dots, setDots] = useState("");
  useEffect(() => {
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 380);
    return () => clearInterval(id);
  }, []);

  const ring = size + 32;

  const wrapper = fullscreen
    ? "fixed inset-0 z-[9999] flex items-center justify-center bg-white dark:bg-[#0B0D10]"
    : "flex items-center justify-center py-16";

  return (
    <div
      className={wrapper}
      role="status"
      aria-live="polite"
      aria-label={label}
      data-testid="branded-loader"
    >
      <div className="flex flex-col items-center gap-5">
        {/* Ring + logo stack */}
        <div
          className="relative flex items-center justify-center"
          style={{ width: ring, height: ring }}
        >
          {/* Rotating crimson conic-gradient ring */}
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, transparent 240deg, #DC262F 320deg, #FF3B30 360deg)",
              animation: "rim-spin 1.4s linear infinite",
              WebkitMask:
                "radial-gradient(circle at center, transparent 60%, black 62%)",
              mask: "radial-gradient(circle at center, transparent 60%, black 62%)",
            }}
          />
          {/* Soft ambient halo behind the mark */}
          <span
            aria-hidden="true"
            className="absolute rounded-full"
            style={{
              width: size + 12,
              height: size + 12,
              background:
                "radial-gradient(closest-side, rgba(220,38,47,0.18), transparent 70%)",
              animation: "rim-pulse 2.2s ease-in-out infinite",
            }}
          />
          {/* Static logo — never rotates so the mark stays legible */}
          <img
            src="/logo.png"
            alt="Rim Task"
            width={size}
            height={size}
            style={{ width: size, height: size, objectFit: "contain" }}
            className="relative z-10 select-none pointer-events-none drop-shadow-sm"
            data-testid="branded-loader-logo"
          />
        </div>

        {/* Caption */}
        <div className="flex items-baseline gap-1 font-mono text-[11px] uppercase tracking-[0.35em] text-[#5C5C5C] dark:text-white/60">
          <span>{label}</span>
          <span
            className="inline-block min-w-[1.2em] text-left text-[#DC262F]"
            data-testid="branded-loader-dots"
          >
            {dots}
          </span>
        </div>
      </div>

      {/* Keyframes scoped locally — no global CSS churn. */}
      <style>{`
        @keyframes rim-spin { to { transform: rotate(360deg); } }
        @keyframes rim-pulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.08); }
        }
      `}</style>
    </div>
  );
}
